import React, { ReactElement, useEffect, useState } from 'react';
import { Link, NavigateFunction } from 'react-router-dom';
import { flowRight as compose } from 'lodash';

import withAuthentication, {
  LOGGED_MODE_USER,
  LOGGED_MODE_GUEST,
} from '../Auth/AuthenticationWrapper';
import s from './WebHeader.module.scss';
import getAssetLink from '../../util/getAssetLink';
import { ROUTE_PREFIX } from '../../constants/router';
import { withRouter } from '../../util/withRouter';
import DropdownMenu from './DropdownMenu';
import { withLogout } from '../Auth/withLogout';
import withInterestTestResults, {
  itestResultsType,
} from '../HomeScreen/DataWrappers/InterestTestResultsWrapper';
import { checkIfTestResultsAreInvalid } from '../HomeScreen/DataWrappers/TestCompletionRequiredWrapper';

export const headerLinks = [
  {
    title: 'WARUM?',
    href: `${ROUTE_PREFIX}/warum-jopsy`,
    ariaLabel: 'Zur "Warum Jopsy?" Unterseite',
  },
  {
    title: 'FACTS',
    href: `${ROUTE_PREFIX}/fakten`,
    ariaLabel: 'Zur "Facts" Unterseite',
  },
  {
    title: 'BERUFE ENTDECKEN',
    href: `${ROUTE_PREFIX}/berufe-entdecken`,
    ariaLabel: 'Zur Berufsliste',
  },
  {
    title: 'ANGEBOTE',
    href: `${ROUTE_PREFIX}/mode-select/angebote`,
    icon: 'austria.png',
    ariaLabel: 'Zu den Angeboten der AK',
  },
];

interface WebHeaderProps {
  userItestResults?: itestResultsType;
  location: Location;
  loggedMode: string;
  logout: () => void;
  navigate: NavigateFunction;
  refetchItestResults: () => Promise<void>;
}

function WebHeader(props: WebHeaderProps): ReactElement {
  const {
    userItestResults,
    location,
    loggedMode,
    logout,
    navigate,
    refetchItestResults,
  } = props;

  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [lastPathName, setLastPathName] = useState('/');

  useEffect(() => {
    if (location.pathname !== lastPathName) {
      setLastPathName(location.pathname);
      setShowMobileMenu(false);
    }
  }, [location.pathname, lastPathName]);

  const isPilot = location.pathname.includes('normalization');
  const isVerification = location.pathname.includes('verification');

  let startUrl: string;
  if (isPilot) {
    startUrl = `${ROUTE_PREFIX}/normalization/identification`;
  } else if (isVerification) {
    startUrl = `${ROUTE_PREFIX}/verification/infographic`;
  } else {
    startUrl = `${ROUTE_PREFIX}/mode-select/interests-test`;
  }

  const testResultsInvalid = checkIfTestResultsAreInvalid(userItestResults);

  const allHeaderLinks = [];

  if (testResultsInvalid) {
    allHeaderLinks.push(
      <button
        key="start"
        className={s.buttonLink}
        type="button"
        onClick={async () => {
          await refetchItestResults();
          navigate(startUrl);
        }}
        aria-label="Starte den Interessenstest"
      >
        <img src={getAssetLink('icon_small.png')} alt="" />
        <span>STARTEN</span>
      </button>,
    );
  } else {
    allHeaderLinks.push(
      <Link
        key="home"
        className={s.link}
        to={`${ROUTE_PREFIX}/home`}
        aria-label="Zu meinen Ergebnissen"
      >
        <img src={getAssetLink('icon_small.png')} alt="" />
        <span>ERGEBNISSE</span>
      </Link>,
    );
  }

  headerLinks.forEach((link) => {
    allHeaderLinks.push(
      <Link
        key={link.href}
        className={s.link}
        to={link.href}
        aria-label={link.ariaLabel}
      >
        {link.icon && <img src={getAssetLink(link.icon)} alt="" />}
        <span>{link.title}</span>
      </Link>,
    );
  });

  if (loggedMode === LOGGED_MODE_USER) {
    allHeaderLinks.push(
      <button
        key="logout"
        className={s.buttonLink}
        type="button"
        onClick={() => {
          logout();
        }}
        aria-label="logout"
      >
        <span>LOGOUT</span>
      </button>,
    );
  } else if (loggedMode === LOGGED_MODE_GUEST) {
    allHeaderLinks.push(
      <Link
        key="login"
        className={s.link}
        to={`${ROUTE_PREFIX}/login`}
        aria-label="login"
      >
        <span>LOGIN</span>
      </Link>,
    );
  }

  return (
    <header className={s.header}>
      <div className={s.headerContent}>
        <nav className={s.headerLogoNav}>
          <Link
            className={s.logoLink}
            to={`${ROUTE_PREFIX}/`}
            aria-label="Zur Startseite"
          >
            <img src={getAssetLink('AKLogo_small.png')} alt="Startseite" />
          </Link>
        </nav>
        <nav className={s.headerLinksNav}>{allHeaderLinks}</nav>
        <DropdownMenu
          className={s.mobileDropdown}
          icon={<img src={getAssetLink('burger_menu.png')} alt="Menü" />}
          show={showMobileMenu}
          onSetActiveMenu={setShowMobileMenu}
        >
          {allHeaderLinks}
        </DropdownMenu>
      </div>
    </header>
  );
}

WebHeader.defaultProps = {
  userItestResults: undefined,
};

export default compose(
  withAuthentication,
  withInterestTestResults,
  withLogout,
  withRouter,
)(WebHeader);
